import React, { useState, useEffect } from "react";
import axios from "axios";

const BaseLocation = () => {
    const [type, setType] = useState("all");
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingLocation, setEditingLocation] = useState(null); // null = add mode
    const [locationName, setLocationName] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const getBaseLocation = async (filterType) => {
        setLoading(true);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_SERVER_ADDRESS}/common/get-baseLocation`,
                {
                    params: { type: filterType },
                },
            );
            setLocations(response.data?.data || response.data || []);
        } catch (error) {
            console.error("Error", error.response?.data?.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getBaseLocation(type);
    }, [type]);

    const openAddModal = () => {
        setEditingLocation(null);
        setLocationName("");
        setIsModalOpen(true);
    };

    const openEditModal = (location) => {
        setEditingLocation(location);
        setLocationName(location.name);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditingLocation(null);
        setLocationName("");
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!locationName.trim()) return;

        setSubmitting(true);
        try {
            const trimmedName = locationName.trim();
            if (editingLocation) {
                await axios.put(
                    `${process.env.REACT_APP_SERVER_ADDRESS}/common/update-baseLocation`,
                    {
                        locationId: editingLocation.id,
                        locationName: trimmedName,
                        isActive: editingLocation.isActive,
                    },
                );
            } else {
                await axios.post(
                    `${process.env.REACT_APP_SERVER_ADDRESS}/common/add-baseLocation`,
                    { locationName: trimmedName },
                );
            }
            closeModal();
            getBaseLocation(type);
        } catch (error) {
            console.error("Error", error.response?.data?.message);
        } finally {
            setSubmitting(false);
        }
    };

    const toggleActive = async (location) => {
        try {
            await axios.put(
                `${process.env.REACT_APP_SERVER_ADDRESS}/common/update-baseLocation`,
                {
                    locationId: location.id,
                    locationName: location.name,
                    isActive: !location.isActive,
                },
            );
            getBaseLocation(type);
        } catch (error) {
            console.error("Error", error.response?.data?.message);
        }
    };

    const filterButtons = [
        { label: "All", value: "all" },
        { label: "Active", value: "active" },
        { label: "Inactive", value: "inactive" },
    ];

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-5 sm:py-8">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                    <div>
                        <h1 className="text-xl sm:text-2xl font-bold text-black">
                            Base Location
                        </h1>
                        <p className="text-sm text-gray-500 mt-0.5">
                            Manage work locations and their status
                        </p>
                    </div>
                    <button
                        onClick={openAddModal}
                        className="inline-flex items-center justify-center gap-2 bg-yellow-400 hover:bg-yellow-500 active:bg-yellow-500 text-black font-semibold px-5 py-2.5 rounded-lg shadow-sm transition-colors w-full sm:w-auto"
                    >
                        <span className="text-lg leading-none">+</span>
                        Add Location
                    </button>
                </div>

                {/* Filter Buttons */}
                <div className="flex gap-2 mb-5 overflow-x-auto pb-1">
                    {filterButtons.map((btn) => (
                        <button
                            key={btn.value}
                            onClick={() => setType(btn.value)}
                            className={`shrink-0 px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                                type === btn.value
                                    ? "bg-black text-yellow-400 border-black"
                                    : "bg-white text-gray-700 border-gray-300 hover:border-black hover:text-black"
                            }`}
                        >
                            {btn.label}
                        </button>
                    ))}
                </div>

                {/* ---------- Desktop / tablet: table ---------- */}
                <div className="hidden sm:block bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-yellow-50 border-b border-gray-200">
                                <th className="px-4 py-3 w-16 font-semibold text-xs uppercase tracking-wide text-gray-600">
                                    S.No
                                </th>
                                <th className="px-4 py-3 font-semibold text-xs uppercase tracking-wide text-gray-600">
                                    Location Name
                                </th>
                                <th className="px-4 py-3 w-32 font-semibold text-xs uppercase tracking-wide text-gray-600">
                                    Status
                                </th>
                                <th className="px-4 py-3 w-56 font-semibold text-xs uppercase tracking-wide text-gray-600 text-right">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {loading ? (
                                <tr>
                                    <td
                                        colSpan={4}
                                        className="px-4 py-10 text-center text-gray-500"
                                    >
                                        Loading...
                                    </td>
                                </tr>
                            ) : locations.length === 0 ? (
                                <tr>
                                    <td
                                        colSpan={4}
                                        className="px-4 py-10 text-center text-gray-500"
                                    >
                                        No locations found
                                    </td>
                                </tr>
                            ) : (
                                locations.map((location, index) => (
                                    <tr
                                        key={location.id}
                                        className="hover:bg-yellow-50/60 transition-colors"
                                    >
                                        <td className="px-4 py-3 align-middle text-gray-500">
                                            {index + 1}
                                        </td>
                                        <td className="px-4 py-3 align-middle text-black font-medium capitalize">
                                            {location.name}
                                        </td>
                                        <td className="px-4 py-3 align-middle">
                                            <span
                                                className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${
                                                    location.isActive
                                                        ? "bg-green-100 text-green-700"
                                                        : "bg-gray-200 text-gray-600"
                                                }`}
                                            >
                                                {location.isActive
                                                    ? "Active"
                                                    : "Inactive"}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3 align-middle">
                                            <div className="flex justify-end gap-2">
                                                <button
                                                    onClick={() =>
                                                        openEditModal(
                                                            location,
                                                        )
                                                    }
                                                    className="px-3 py-1.5 rounded-md border border-gray-300 text-black hover:border-black hover:bg-yellow-50 text-sm font-medium transition-colors"
                                                >
                                                    Update
                                                </button>
                                                <button
                                                    onClick={() =>
                                                        toggleActive(
                                                            location,
                                                        )
                                                    }
                                                    className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                                                        location.isActive
                                                            ? "bg-black text-white hover:bg-gray-800"
                                                            : "bg-yellow-400 text-black hover:bg-yellow-500"
                                                    }`}
                                                >
                                                    {location.isActive
                                                        ? "Deactivate"
                                                        : "Activate"}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* ---------- Mobile: card list ---------- */}
                <div className="sm:hidden space-y-3">
                    {loading ? (
                        <div className="bg-white border border-gray-200 rounded-xl py-10 text-center text-gray-500">
                            Loading...
                        </div>
                    ) : locations.length === 0 ? (
                        <div className="bg-white border border-gray-200 rounded-xl py-10 text-center text-gray-500">
                            No locations found
                        </div>
                    ) : (
                        locations.map((location, index) => (
                            <div
                                key={location.id}
                                className="bg-white border border-gray-200 rounded-xl shadow-sm p-4"
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <div>
                                        <p className="text-xs text-gray-400">
                                            #{index + 1}
                                        </p>
                                        <p className="text-black font-semibold">
                                            {location.name}
                                        </p>
                                    </div>
                                    <span
                                        className={`shrink-0 inline-flex px-3 py-1 rounded-full text-xs font-semibold ${
                                            location.isActive
                                                ? "bg-green-100 text-green-700"
                                                : "bg-gray-200 text-gray-600"
                                        }`}
                                    >
                                        {location.isActive
                                            ? "Active"
                                            : "Inactive"}
                                    </span>
                                </div>

                                <div className="flex gap-2 mt-3">
                                    <button
                                        onClick={() =>
                                            openEditModal(location)
                                        }
                                        className="flex-1 px-3 py-2 rounded-md border border-gray-300 text-black hover:border-black hover:bg-yellow-50 text-sm font-medium transition-colors"
                                    >
                                        Update
                                    </button>
                                    <button
                                        onClick={() =>
                                            toggleActive(location)
                                        }
                                        className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                                            location.isActive
                                                ? "bg-black text-white hover:bg-gray-800"
                                                : "bg-yellow-400 text-black hover:bg-yellow-500"
                                        }`}
                                    >
                                        {location.isActive
                                            ? "Deactivate"
                                            : "Activate"}
                                    </button>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* Add / Update Modal */}
                {isModalOpen && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl w-full max-w-md p-6 shadow-xl">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-bold text-black">
                                    {editingLocation
                                        ? "Update Location"
                                        : "Add Location"}
                                </h2>
                                <button
                                    onClick={closeModal}
                                    className="text-gray-400 hover:text-black text-xl leading-none"
                                >
                                    &times;
                                </button>
                            </div>

                            <form onSubmit={handleSubmit}>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Location Name
                                </label>
                                <input
                                    type="text"
                                    value={locationName}
                                    onChange={(e) =>
                                        setLocationName(e.target.value)
                                    }
                                    placeholder="e.g. malanpur, gwalior"
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2.5 mb-5 text-black focus:outline-none focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400"
                                    autoFocus
                                />

                                <div className="flex justify-end gap-2">
                                    <button
                                        type="button"
                                        onClick={closeModal}
                                        className="px-4 py-2 rounded-lg border border-gray-300 text-black hover:bg-gray-100 transition-colors"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="px-4 py-2 rounded-lg bg-yellow-400 hover:bg-yellow-500 text-black font-semibold disabled:opacity-50 transition-colors"
                                    >
                                        {submitting
                                            ? "Saving..."
                                            : editingLocation
                                              ? "Update"
                                              : "Add"}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BaseLocation;